import params from '@config';

export class HeartsService {
  constructor(configuration) {
    this.baseUrl = new URL(configuration.hearts_service);
    this.subscribers = new Map();
    this.activeSubscriptions = new Set();
    this.reconnectTries = 10;
    this.attempts = 0;
    this.ws = null;
    this.reconnectTimer = null;
    this.pingTimer = null;
  }

  connect() {
    if (!this.subscribers.size || this.ws || this.reconnectTimer !== null) return;

    const socketUrl = this.websocketURL();
    let ws;
    try {
      ws = new WebSocket(socketUrl);
    } catch (error) {
      console.error(`Failed to connect hearts at ${socketUrl}`, error);
      this.scheduleReconnect();
      return;
    }
    this.ws = ws;

    ws.addEventListener('open', () => {
      if (this.ws !== ws) return;
      this.attempts = 0;
      this.activeSubscriptions.clear();
      for (const id of this.subscribers.keys()) this.sendSubscribe(ws, id);
      this.pingTimer = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) ws.send('');
      }, 5000);
    });

    ws.addEventListener('message', (event) => {
      if (this.ws !== ws) return;
      for (const handlers of this.subscribers.values()) {
        for (const handler of handlers) handler(event);
      }
    });

    ws.addEventListener('close', () => {
      if (this.ws !== ws) return;
      this.ws = null;
      clearInterval(this.pingTimer);
      this.pingTimer = null;
      this.activeSubscriptions.clear();
      this.scheduleReconnect();
    });

    ws.addEventListener('error', (event) => {
      console.error(`WebSocket error for ${socketUrl}`, event);
    });
  }

  scheduleReconnect() {
    if (!this.subscribers.size || this.reconnectTimer !== null || this.attempts >= this.reconnectTries) return;
    this.attempts += 1;
    const delaySeconds = this.attempts ** 2 + 10;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delaySeconds * 1000);
  }

  stop() {
    clearTimeout(this.reconnectTimer);
    clearInterval(this.pingTimer);
    this.reconnectTimer = null;
    this.pingTimer = null;
    this.attempts = 0;
    this.activeSubscriptions.clear();
    const ws = this.ws;
    this.ws = null;
    ws?.close();
  }

  websocketURL() {
    const wsUrl = new URL(this.baseUrl);
    if (wsUrl.protocol === 'http:') wsUrl.protocol = 'ws:';
    else if (wsUrl.protocol === 'https:') wsUrl.protocol = 'wss:';
    else throw new Error(`Unknown protocol ${wsUrl.protocol}`);
    return wsUrl.toString();
  }

  sendSubscribe(ws, id) {
    if (this.activeSubscriptions.has(id)) return;
    ws.send(JSON.stringify({ method: 'SUBSCRIBE', id }));
    this.activeSubscriptions.add(id);
  }

  subscribe(id, handler) {
    let handlers = this.subscribers.get(id);
    if (!handlers) {
      handlers = new Set();
      this.subscribers.set(id, handlers);
    }
    handlers.add(handler);
    this.connect();
    if (this.ws?.readyState === WebSocket.OPEN) this.sendSubscribe(this.ws, id);
    return () => this.unsubscribe(id, handler);
  }

  unsubscribe(id, handler) {
    const handlers = this.subscribers.get(id);
    if (!handlers) return;
    handlers.delete(handler);
    if (!handlers.size) {
      this.subscribers.delete(id);
      this.activeSubscriptions.delete(id);
    }
    if (!this.subscribers.size) this.stop();
  }

  resourceURL(id) {
    return new URL(id, this.baseUrl);
  }

  async requestCount(id, options) {
    const response = await fetch(this.resourceURL(id), options);
    if (!response.ok) throw new Error(`Failed to load hearts for ${id} (${response.status})`);
    const body = (await response.text()).trim();
    const value = Number(body);
    if (!body || !Number.isSafeInteger(value) || value < 0) throw new Error(`Invalid hearts count for ${id}`);
    return value;
  }

  fetchCount(id) {
    return this.requestCount(id, { priority: 'low' });
  }

  increment(id) {
    return this.requestCount(id, { method: 'POST' });
  }
}

export default new HeartsService(params);
